/**
 * The firm's active skills for the composer's skill picker: id, slug, title,
 * description. Fetched once per signed-in user and shared across every
 * mounted Case IDE panel; `invalidateActiveFirmSkills` (a builder turn wrote a
 * skill, the Skills tab enabled one) refetches for all of them. Signing in as
 * someone else (another firm) drops the list; a failed load is retried a few
 * times rather than leaving the picker empty for the session.
 */
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";

import { firmKnowledgeApi } from "@/api/firm-knowledge";
import { auth } from "@/api/firebaseApi";

import type { FirmSkillSummary } from "../types";
import { skillRunLine } from "../skillRunText";

type Listener = (skills: FirmSkillSummary[]) => void;

const RETRY_DELAYS_MS = [2000, 8000, 30000];

let cache: FirmSkillSummary[] | null = null;
let cacheOwner: string | null = null;
let inflight: Promise<FirmSkillSummary[]> | null = null;
let failures = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let unsubscribeAuth: (() => void) | null = null;
const listeners = new Set<Listener>();

const currentUid = (): string | null => auth.currentUser?.uid ?? null;

const publish = (skills: FirmSkillSummary[]) =>
  listeners.forEach((l) => l(skills));

const scheduleRetry = (reload: () => Promise<unknown>) => {
  if (retryTimer || failures > RETRY_DELAYS_MS.length || !listeners.size)
    return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (!cache && listeners.size) void reload();
  }, RETRY_DELAYS_MS[failures - 1]);
};

const load = (): Promise<FirmSkillSummary[]> => {
  if (inflight) return inflight;
  const owner = currentUid();
  inflight = firmKnowledgeApi
    .listActiveSkillSummaries()
    .then((skills) => {
      if (owner !== currentUid()) return skills;
      cache = skills;
      cacheOwner = owner;
      failures = 0;
      publish(skills);
      return skills;
    })
    .catch((e) => {
      Sentry.captureException(e);
      failures += 1;
      scheduleRetry(load);
      return cache ?? [];
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

const watchAuth = () => {
  if (unsubscribeAuth) return;
  unsubscribeAuth = auth.onAuthStateChanged((user) => {
    const uid = user?.uid ?? null;
    if (uid === cacheOwner) return;
    cache = null;
    cacheOwner = null;
    failures = 0;
    publish([]);
    if (uid && listeners.size) void load();
  });
};

/** Refetch for every mounted picker (after a skill was written or enabled). */
export const invalidateActiveFirmSkills = (): void => {
  cache = null;
  failures = 0;
  void load();
};

/** Test seam: drop the shared cache. */
export const resetActiveFirmSkillsCache = (): void => {
  cache = null;
  cacheOwner = null;
  inflight = null;
  failures = 0;
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  unsubscribeAuth?.();
  unsubscribeAuth = null;
  listeners.clear();
};

export const useActiveFirmSkills = (): FirmSkillSummary[] => {
  const fresh = cache && cacheOwner === currentUid() ? cache : null;
  const [skills, setSkills] = useState<FirmSkillSummary[]>(fresh ?? []);
  useEffect(() => {
    watchAuth();
    listeners.add(setSkills);
    if (cache && cacheOwner === currentUid()) setSkills(cache);
    else void load();
    return () => {
      listeners.delete(setSkills);
    };
  }, []);
  return skills;
};

/** The first message a run's thread sends (the plain-wording form of `firm_skill_service.seed_message_for`). */
export const skillRunSeed = (title: string): string => skillRunLine(title);
